import { useContext, useMemo, useState } from "react"

import { Trans, useTranslation } from "react-i18next"
import { useQuery } from "react-query"
import { useSelector } from "react-redux"
import { useParams } from "react-router-dom"

import { Grid } from "@mui/material"
import { Select } from "@synapse-analytics/synapse-ui"
import type { AxiosError, AxiosResponse } from "axios"
import { isEmpty as _isEmpty } from "lodash"

import { ScatterPlot } from "../../../components/graphs/ScatterPlot/ScatterPlot"
import { KonanAPI } from "../../../services/KonanAPI"
import type { RootState } from "../../../store/ReduxStore"
import { InsightList } from "../../../types/generated/api/InsightList"
import { FEATURE_COMPARISON_DELIMITER, FEATURE_NESTING_SEPARATOR } from "../../../utils/constants"
import { type NestedRecord, extractDeepestKeys } from "../../../utils/genericHelpers"
import { ReportContext } from "../Context"
import type { CorrelationInsightProps, InsightProps } from "../interfaces"

/**
 * Props for the CorrelationInsight component.
 *
 * @property {InsightProps} insight - The insight data passed to the component.
 *   - insight: Insight metadata including:
 *     - uuid: Unique identifier for the insight
 *     - status: Current status of the insight (e.g., SUCCEEDED, FAILED)
 *
 * @returns {React.ReactElement} A Grid item containing the CorrelationInsight visualization
 */
export function CorrelationInsight(props: Readonly<InsightProps>): React.ReactElement {
  const { insight, isFeedback } = props
  const { id: projectId } = useParams<{ id: string }>() // Extract project ID from URL params

  // Access the label breakdowns from the Redux store
  const labelReasons = useSelector((state: RootState) =>
    isFeedback ? state.reports.labelsCriteria : state.reports.labelsBreakdown,
  )

  const { report } = useContext(ReportContext) // Access the report context from the provider

  const { t } = useTranslation("insights")

  // State variables to manage selected labels and reasons for comparison
  const [leftSideLabel, setLeftSideLabel] = useState<string | null>(null)
  const [leftSideReason, setLeftSideReason] = useState<string | null>("All reasons")
  const [rightSideLabel, setRightSideLabel] = useState<string | null>(null)
  const [rightSideReason, setRightSideReason] = useState<string | null>("All reasons")

  // Fetch correlation insight data using react-query
  const { data: insightData, isLoading: isLoadingCorrelationInsight } = useQuery<
    AxiosResponse<CorrelationInsightProps>,
    AxiosError
  >(
    ["correlation", projectId, report?.uuid, insight.uuid],
    () =>
      KonanAPI.fetchInsight({
        project_uuid: projectId as string,
        report_uuid: report?.uuid as string,
        insight_uuid: insight.uuid,
      }),
    {
      enabled: !!report?.uuid && !!projectId && !!insight.uuid && insight.status === InsightList.status.SUCCEEDED,
      refetchOnMount: false,
    },
  )

  const isEmpty =
    extractDeepestKeys((labelReasons?.data as { [key: string]: number | NestedRecord }) ?? {}).length < 2 ||
    _isEmpty(insightData?.data.data)

  const labelsAndReasons = useMemo((): { reasons: Record<string, string[]>; labels: string[] } => {
    const reasons: Record<string, string[]> = {}
    const labels: Array<string> = isFeedback
      ? extractDeepestKeys((labelReasons?.data as { [key: string]: number | NestedRecord }) ?? {})
      : Object.keys(labelReasons?.data ?? {})

    if (!labelReasons) return { reasons: reasons, labels: [] }

    if (!isFeedback) {
      Object.entries(labelReasons?.data).forEach(([label, labelDetails]) => {
        reasons[label] = labelDetails.reasons.map((reason: { count: number; reason: string }) => reason.reason)
      })
    }

    if (labelReasons && !rightSideLabel && !leftSideLabel) {
      setLeftSideLabel(labels[0] ?? "")
      setRightSideLabel(labels[1] ?? "")
    }

    return { reasons: reasons, labels: labels }
  }, [isFeedback, labelReasons, leftSideLabel, rightSideLabel])

  // Adjust the insight data into the format required for the scatter plot
  const adjustedData = useMemo(() => {
    const data: Array<{
      id: string
      data: Array<{ x: number; y: string; bin: string; decision?: string; reason?: string }>
    }> = []

    if (isLoadingCorrelationInsight || !labelReasons || !leftSideLabel || !rightSideLabel || isEmpty) return []

    const leftSideSelector = `${leftSideLabel}${!!leftSideReason && leftSideReason !== "All reasons" ? `${FEATURE_COMPARISON_DELIMITER}${leftSideReason}` : ""}`
    const rightSideSelector = `${rightSideLabel}${!!rightSideReason && rightSideReason !== "All reasons" ? `${FEATURE_COMPARISON_DELIMITER}${rightSideReason}` : ""}`

    Object.entries(insightData?.data.data[rightSideSelector]?.[leftSideSelector] ?? {})?.forEach(([pKey, value]) => {
      const entry: Array<{ x: number; y: string; bin: string; decision?: string; reason?: string }> = []

      Object.entries(value).forEach(([key, value]) => {
        if (value) {
          const metaData =
            value === 0
              ? undefined
              : value > 0
                ? { decision: rightSideLabel, reason: rightSideReason ?? undefined }
                : { decision: leftSideLabel, reason: leftSideReason ?? undefined }

          entry.push({
            x: value,
            y: pKey.replaceAll(FEATURE_NESTING_SEPARATOR, "."),
            bin: report?.features?.numerical?.includes(pKey) ? key.toString().replace("__", "->") : key,
            decision: metaData?.decision,
            reason: metaData?.reason,
          })
        }
      })

      // Sort the entry array by x value (correlation strength)
      entry.sort((a, b) => Math.abs(b.x) - Math.abs(a.x))

      if (entry.length > 0) data.push({ id: pKey, data: entry })
    })

    // Sort the overall data array by the maximum absolute correlation value
    data.sort((a, b) => {
      const maxAbsA = Math.max(...a.data.map((d) => Math.abs(d.x)))
      const maxAbsB = Math.max(...b.data.map((d) => Math.abs(d.x)))
      return maxAbsB - maxAbsA
    })

    return data
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    insightData?.data.data,
    isLoadingCorrelationInsight,
    labelReasons,
    leftSideLabel,
    leftSideReason,
    rightSideLabel,
    rightSideReason,
  ])

  /**
   * Subheader component that displays label and reason selection controls.
   * @returns {React.ReactElement} The subheader element containing select inputs for labels and reasons.
   */
  const subHeader = (): React.ReactElement => {
    return (
      <Grid container columnSpacing={1}>
        <Grid item xs={3}>
          <Select
            label={
              isFeedback ? t("Correlation.subHeader.leftSide.feedback") : t("Correlation.subHeader.leftSide.decision")
            }
            options={labelsAndReasons.labels ?? []}
            value={leftSideLabel ?? ""}
            handleChange={(e) => {
              setLeftSideLabel(e.target.value as string)
              setLeftSideReason("All reasons")
            }}
            isLoadingOptions={isLoadingCorrelationInsight}
            fullWidth
            menuProps={{ menuMaxContent: true }}
          />
        </Grid>

        {!isFeedback && (
          <Grid item xs={3}>
            <Select
              label={t("Correlation.subHeader.leftSide.reason")}
              optionsWithValues={[
                { value: "All reasons", label: t("Correlation.subHeader.allReasons") },
                ...(labelsAndReasons.reasons[leftSideLabel as string] ?? []).map((item: string) => {
                  return { value: item, label: item }
                }),
              ]}
              value={leftSideReason ?? ""}
              handleChange={(e) => setLeftSideReason(e.target.value as string)}
              isLoadingOptions={isLoadingCorrelationInsight}
              fullWidth
              hideDescription
              menuProps={{ menuMaxContent: true }}
            />
          </Grid>
        )}

        <Grid item xs={3}>
          <Select
            label={
              isFeedback ? t("Correlation.subHeader.rightSide.feedback") : t("Correlation.subHeader.rightSide.decision")
            }
            options={labelsAndReasons.labels ?? []}
            value={rightSideLabel ?? ""}
            handleChange={(e) => {
              setRightSideLabel(e.target.value as string)
              setRightSideReason("All reasons")
            }}
            isLoadingOptions={isLoadingCorrelationInsight}
            fullWidth
            hideDescription
            menuProps={{ menuMaxContent: true }}
          />
        </Grid>

        {!isFeedback && (
          <Grid item xs={3}>
            <Select
              label={t("Correlation.subHeader.rightSide.reason")}
              optionsWithValues={[
                { value: "All reasons", label: t("Correlation.subHeader.allReasons") },
                ...(labelsAndReasons.reasons[rightSideLabel as string] ?? []).map((item: string) => {
                  return { value: item, label: item }
                }),
              ]}
              value={rightSideReason ?? ""}
              handleChange={(e) => setRightSideReason(e.target.value as string)}
              isLoadingOptions={isLoadingCorrelationInsight}
              fullWidth
              hideDescription
              menuProps={{ menuMaxContent: true }}
            />
          </Grid>
        )}
      </Grid>
    )
  }

  // Render the main component
  return (
    <Grid item xs={12}>
      <ScatterPlot
        title={t("Correlation.title")}
        tooltipText={<Trans>{t("Correlation.tooltip")}</Trans>}
        data={adjustedData ?? {}}
        subHeader={!isEmpty ? subHeader() : undefined}
        isLoading={isLoadingCorrelationInsight}
        graphHeight={440}
        axisTop={{
          format: (value) => {
            return value === -1 ? leftSideLabel : value === 1 ? rightSideLabel : ""
          },
        }}
        emptyState={
          insight.status !== InsightList.status.SUCCEEDED
            ? {
                title: `${t("label.insight")} ${t(`status.${insight.status?.toLocaleLowerCase()}`, {
                  defaultValue: insight.status,
                })?.toLocaleLowerCase()}`,
              }
            : isEmpty
              ? { title: t("Correlation.unavailable") }
              : undefined
        }
      />
    </Grid>
  )
}
